import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import '../styles/Reports.css';

function Reports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ageLimitDays, setAgeLimitDays] = useState(null);
  const { token, user, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchReports();
    if (!isAdmin) {
      fetchAgeLimit();
    }
  }, []);

  const fetchReports = async () => {
    try {
      const data = await api.getReports(token);
      setReports(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgeLimit = async () => {
    try {
      const data = await api.getReportAgeLimit(token);
      setAgeLimitDays(data.days);
    } catch (err) {
      console.error('Error fetching age limit:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Er du sikker på at du vil slette denne rapporten?')) {
      return;
    }

    try {
      await api.deleteReport(token, id);
      setReports(reports.filter(r => r.id !== id));
    } catch (err) {
      alert('Feil ved sletting: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('no-NO', {
      timeZone: 'Europe/Oslo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';

    // Parse the timestamp from database
    // SQLite stores as "YYYY-MM-DD HH:MM:SS" in UTC
    // Append 'Z' if not present to ensure it's parsed as UTC
    const dateStr = dateString.includes('Z') ? dateString : dateString + 'Z';
    const date = new Date(dateStr);

    // Verify date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date:', dateString);
      return dateString;
    }

    // Format using Norwegian locale and timezone (Europe/Oslo)
    const datePart = date.toLocaleDateString('no-NO', {
      timeZone: 'Europe/Oslo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const timePart = date.toLocaleTimeString('no-NO', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Europe/Oslo'
    });
    return `${datePart} ${timePart}`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    return timeString.substring(0, 5);
  };

  const canDeleteReport = (report) => {
    // Admin can delete all reports, users can only delete their own
    return isAdmin || report.user_id === user?.id;
  };

  if (loading) {
    return <div className="loading">Laster rapporter...</div>;
  }

  return (
    <div className="reports-container">
      <div className="reports-header">
        <Link to="/reports/new" className="btn-primary">
          Ny rapport
        </Link>
      </div>

      {error && <div className="error-message">{error}</div>}

      {reports.length === 0 ? (
        <div className="empty-state">
          <p>Ingen rapporter funnet</p>
          <Link to="/reports/new" className="btn-primary">
            Opprett første rapport
          </Link>
        </div>
      ) : (
        <div className="reports-table-container">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Skifttid</th>
                <th>Bruker</th>
                <th>Opprettet</th>
                <th>Sist endret</th>
                <th>Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {reports.map(report => (
                <tr key={report.id}>
                  <td data-label="Skifttid">
                    {formatDate(report.shift_date)} {formatTime(report.shift_start_time)} - {formatTime(report.shift_end_time)}
                  </td>
                  <td data-label="Bruker">{report.full_name}</td>
                  <td data-label="Opprettet">{formatDateTime(report.created_at)}</td>
                  <td data-label="Sist endret">{formatDateTime(report.updated_at)}</td>
                  <td data-label="Handlinger">
                    <button
                      onClick={() => navigate(`/reports/${report.id}`)}
                      className="btn-small"
                    >
                      Se rapport
                    </button>
                    {canDeleteReport(report) && (
                      <button
                        onClick={() => handleDelete(report.id)}
                        className="btn-small btn-danger"
                      >
                        Slett
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Reports;
